// AID 清冊 / 供應鏈 API layer(S28)。後端 = ca-manager(本案以 ca-manager 為主),
// 經 restful 白名單代理 /v3/admin/aids*(沿用 admin 既有命名;代理 → ca-manager
// /admin/aids)。回應 {data}/{error} 信封 + cursor 分頁。不回 private key。
import { apiFetch, normalizeCursor } from '../../lib/api';

export type AidStatus = 'available' | 'allocated' | 'paired';

/** 一筆 AID(= device_cert record;did = 憑證 CN)。不含 private key 材料。 */
export interface Aid {
  did: string;
  type: string;
  serial: string;
  alloc_status: AidStatus | string;
  po_number?: string | null;
  org_id?: string | null;
  mac?: string | null;
  not_before?: string | null;
  not_after?: string | null;
  created_at?: string | null;
  allocated_at?: string | null;
  paired_at?: string | null;
  import_id?: string | null;
}

export interface ListAidParams {
  type?: string;
  status?: string;
  po_number?: string;
  org_id?: string;
  q?: string; // did 前綴
  cursor?: string;
  limit?: number;
}

export interface AidPage {
  items: Aid[];
  nextCursor: string | null;
}

/** device-ca 權威 activation binding / RMA replacement chain；只有公開 metadata。 */
export interface DeviceCaAid {
  aid_id: string;
  device_id: string;
  aid_level_id: string;
  aid_level_version: number;
  status: string;
  model?: string;
  usage?: string;
  issued_at?: string;
  activated_at?: string;
  expires_at?: string;
  replaces_aid_id?: string;
  batch_id?: string;
}

/** GET /v3/admin/aids — 清冊(空篩選被 drop)。回 {items, next_cursor}。 */
export async function listAids(params: ListAidParams = {}): Promise<AidPage> {
  const res = await apiFetch<{ items?: Aid[]; next_cursor?: string }>('/v3/admin/aids', {
    query: {
      type: params.type,
      status: params.status,
      po_number: params.po_number,
      org_id: params.org_id,
      q: params.q,
      cursor: params.cursor,
      limit: params.limit,
    },
  });
  return {
    items: Array.isArray(res.data?.items) ? res.data.items : [],
    nextCursor: normalizeCursor(res.data?.next_cursor),
  };
}

/** GET /v3/admin/aids/{did} — 單筆明細(不含 private key)。 */
export async function getAid(did: string): Promise<Aid> {
  const res = await apiFetch<Aid>(`/v3/admin/aids/${encodeURIComponent(did)}`);
  return res.data;
}

/** GET /v3/admin/pki/devices/{did}/aid — device-ca 權威 AID↔DID 啟用綁定。 */
export async function getDeviceCaAid(did: string): Promise<DeviceCaAid> {
  const res = await apiFetch<DeviceCaAid>(`/v3/admin/pki/devices/${encodeURIComponent(did)}/aid`);
  return res.data;
}

/** GET /v3/admin/pki/aids/{aid_id}/lineage — root→newest 的完整 RMA chain。 */
export async function getDeviceCaAidLineage(aidId: string): Promise<DeviceCaAid[]> {
  const res = await apiFetch<{ items?: DeviceCaAid[] }>(`/v3/admin/pki/aids/${encodeURIComponent(aidId)}/lineage`);
  return Array.isArray(res.data?.items) ? res.data.items : [];
}

export function aidStatusLabel(s?: string): string {
  switch (s) {
    case 'available':
      return '可用(在池)';
    case 'allocated':
      return '已配給';
    case 'paired':
      return '已配對';
    default:
      return s ?? '—';
  }
}

export function aidStatusTone(s?: string): string {
  switch (s) {
    case 'available':
      return 'pill-good';
    case 'allocated':
      return 'pill-info';
    case 'paired':
      return 'pill-neutral';
    default:
      return 'pill-neutral';
  }
}
